import { PosState } from "@unleashed-admin/stores/PosSlice";
import {
  UpgradeTicketViewModel,
  TicketUpgradeDataModel,
  CartItem,
  PrePurchasedItem,
  Product,
} from "../models";
import {
  getProductIdOrTicketIdFromProduct,
  updateStateForPrePurchasedItem,
} from "./Product";
import { getPriceDifference, isTicket } from "./Ticket";

export const areUpgradedTicketsEqual = (
  currentUpgradedTicket: UpgradeTicketViewModel,
  upgradedTicketToCheck: UpgradeTicketViewModel
): boolean => {
  return (
    currentUpgradedTicket.upgradedTicketId ===
      upgradedTicketToCheck.upgradedTicketId &&
    currentUpgradedTicket.totalPrice === upgradedTicketToCheck.totalPrice
  );
};

export const findSimilarUpgradedTicket = (
  upgradedTickets: (TicketUpgradeDataModel | UpgradeTicketViewModel)[],
  productToCheck: CartItem
): TicketUpgradeDataModel | UpgradeTicketViewModel | undefined => {
  return upgradedTickets.find(
    (
      currentUpgradedTicket: TicketUpgradeDataModel | UpgradeTicketViewModel
    ) => {
      if (
        "upgradedTicketId" in currentUpgradedTicket &&
        currentUpgradedTicket.upgradedTicketId !==
          getProductIdOrTicketIdFromProduct(productToCheck)
      ) {
        return false;
      }

      if (
        "upgradedTicketPrice" in currentUpgradedTicket &&
        currentUpgradedTicket.upgradedTicketPrice &&
        (currentUpgradedTicket.upgradedTicketPrice ?? 0) !==
          productToCheck.price
      ) {
        return false;
      }

      return !(
        "totalPrice" in currentUpgradedTicket &&
        currentUpgradedTicket.totalPrice?.toFixed(2) !==
          productToCheck.price?.toFixed(2)
      );
    }
  );
};

export const isSameUpgradedTicket = (
  upgradedTicket: TicketUpgradeDataModel | UpgradeTicketViewModel,
  productToCheck: CartItem
): boolean => {
  if (!upgradedTicket || !productToCheck) {
    return false;
  }
  return (
    findSimilarUpgradedTicket([upgradedTicket], productToCheck) !== undefined
  );
};

export const filterUpgradedRedeemingTickets = (
  items: PrePurchasedItem[],
  ticketUpgradeData: TicketUpgradeDataModel[]
) => {
  // Collect all upgrade ticketIds as numbers
  const upgradeTicketIds = ticketUpgradeData
    .filter((data) => !data.isAdmitted)
    .map((data) => data.ticketId);

  return items
    .map((item: PrePurchasedItem) => {
      if (!isTicket(item)) return item;

      let quantityToReduce = 0;
      // Reduce quantity for each matching upgrade
      for (let i = 0; i < item.quantity; i++) {
        const upgradeIdx = upgradeTicketIds.findIndex(
          (id) => id === item.ticketId
        );
        if (upgradeIdx !== -1) {
          upgradeTicketIds.splice(upgradeIdx, 1);
          quantityToReduce++;
        } else {
          break;
        }
      }

      return { ...item, quantity: item.quantity - quantityToReduce };
    })
    .filter((item) => item.quantity > 0);
};

export const updateItemsInCartForTicketUpgrade = (state: PosState) => {
  state.ticketUpgradeData.forEach((data: TicketUpgradeDataModel) => {
    const cartUpgradedTicket = state.itemsInCart?.find(
      (item: CartItem) => item.uuid === data.uuid
    );

    const upgradedTicket = state.products?.find(
      (product: Product) =>
        isTicket(product) &&
        getProductIdOrTicketIdFromProduct(product) === data.upgradedTicketId
    );

    if (!cartUpgradedTicket && upgradedTicket) {
      const ticketToAdd: CartItem = {
        ...upgradedTicket,
        uuid: data.uuid,
        price: Number(
          getPriceDifference(
            data.ticketId,
            data.upgradedTicketId ?? 0,
            state.products ?? []
          )
        ),
        quantity: 1,
        isUpgradedCartTicket: true,
      };
      state.itemsInCart?.push(ticketToAdd);
    }

    if (
      cartUpgradedTicket &&
      upgradedTicket &&
      cartUpgradedTicket.ticketId !== data.upgradedTicketId
    ) {
      state.itemsInCart = state.itemsInCart?.filter((item: CartItem) => {
        return item.uuid !== data.uuid;
      });
      const ticketToUpdate: CartItem = {
        ...upgradedTicket,
        uuid: data.uuid,
        price: Number(
          getPriceDifference(
            data.ticketId,
            data.upgradedTicketId ?? 0,
            state.products ?? []
          )
        ),
        quantity: 1,
        isUpgradedCartTicket: true,
      };
      state.itemsInCart?.push(ticketToUpdate);
    }
  });

  const upgradedTicketIds = state.ticketUpgradeData.map((data) => data.uuid);

  state.itemsInCart = state.itemsInCart?.filter((item: CartItem) => {
    if (
      isTicket(item) &&
      item.isUpgradedCartTicket &&
      upgradedTicketIds.includes(item.uuid ?? "")
    )
      return true;

    if (isTicket(item) && !item.isUpgradedCartTicket) return true;

    return !isTicket(item);
  });
};

/** Applies ticket upgrades to state on save from upgradePassModal */
export const applyTicketUpgradeChanges = (
  state: PosState,
  ticketUpgradeSnapshot: TicketUpgradeDataModel[]
) => {
  state.ticketUpgradeData = ticketUpgradeSnapshot;
  updateItemsInCartForTicketUpgrade(state);
};

export const removeRedeemedTicketsBasedOnUpgrade = (
  state: PosState,
  ticketId: number,
  upgradedQuantity: number
) => {
  const ticket = state.itemsForRedemption.find(
    (item: PrePurchasedItem) =>
      getProductIdOrTicketIdFromProduct(item) === ticketId
  );

  if (ticket) {
    updateStateForPrePurchasedItem(state, {
      ...ticket,
      quantity: ticket.quantity - upgradedQuantity,
    });
  }
};

// Helper function to find and handle upgraded ticket removal
export const handleUpgradedTicketRemoval = (
  state: PosState,
  existingProduct: CartItem,
  isQuantityUpdate: boolean
) => {
  if (!isTicket(existingProduct) || !existingProduct.isUpgradedCartTicket) {
    return;
  }

  if (isQuantityUpdate) {
    handleSingleUpgradedTicketRemoval(state, existingProduct);
  } else {
    handleAllUpgradedTicketRemoval(state, existingProduct);
  }
};

// Helper function to handle single upgraded ticket removal (quantity update)
export const handleSingleUpgradedTicketRemoval = (
  state: PosState,
  existingProduct: CartItem
) => {
  // Find non-admitted ticket first (redeeming ticket)
  let index = findUpgradedTicketIndex(state, existingProduct, false);

  if (index !== -1) {
    const ticketUpgradeDataItems = state.ticketUpgradeData.find(
      (data: TicketUpgradeDataModel) =>
        isSameUpgradedTicket(data, existingProduct) && !data.isAdmitted
    );
    if (ticketUpgradeDataItems) {
      removeRedeemedTicketsBasedOnUpgrade(
        state,
        ticketUpgradeDataItems.ticketId,
        1
      );
    }
  } else {
    // If not found, find admitted ticket
    index = findUpgradedTicketIndex(state, existingProduct, true);
  }

  if (index !== -1) {
    state.ticketUpgradeData.splice(index, 1);
    applyTicketUpgradeChanges(state, [...state.ticketUpgradeData]);
  }
};

// Helper function to handle all upgraded ticket removal (complete removal)
export const handleAllUpgradedTicketRemoval = (
  state: PosState,
  existingProduct: CartItem
) => {
  const ticketUpgradeDataItems = state.ticketUpgradeData.filter(
    (data: TicketUpgradeDataModel) =>
      isSameUpgradedTicket(data, existingProduct) && !data.isAdmitted
  );

  if (ticketUpgradeDataItems.length > 0 && ticketUpgradeDataItems[0]) {
    removeRedeemedTicketsBasedOnUpgrade(
      state,
      ticketUpgradeDataItems[0].ticketId,
      ticketUpgradeDataItems.length
    );
  }

  state.ticketUpgradeData = state.ticketUpgradeData.filter(
    (ticketUpgradeData: TicketUpgradeDataModel) =>
      !isSameUpgradedTicket(ticketUpgradeData, existingProduct)
  );

  applyTicketUpgradeChanges(state, [...state.ticketUpgradeData]);
};

// Helper function to find upgraded ticket index
export const findUpgradedTicketIndex = (
  state: PosState,
  existingProduct: CartItem,
  isAdmitted: boolean
): number => {
  return state.ticketUpgradeData.findIndex(
    (ticketUpgradeData: TicketUpgradeDataModel) =>
      isSameUpgradedTicket(ticketUpgradeData, existingProduct) &&
      ticketUpgradeData.isAdmitted === isAdmitted
  );
};
